import React from "react";
import { X } from "lucide-react";
import IMAGES from "../../../../constant/images";

const PractitionerProfileDialog = ({ open, onClose, counselor }) => {
  if (!open || !counselor) return null;

  const {
    imageURL,
    firstName,
    lastName,
    title = "Dr.",
    experience,
    rating,
    languages = [],
    degree = null,
    specializations = [],
    consultationCount,
    modes,
    address,
    city,
    state,
    about,
    latestRatings = [],
    nextAvailableSlot,
  } = counselor;

  // Format description from 'about' field
  const description = about || "NOT AVAILABLE";

  // Format hospital/location from address, city, state
  const hospital =
    [address, city, state].map((part) => part && part.trim()).filter(Boolean).join(", ") ||
    "NOT AVAILABLE";

  // Get review count from latestRatings
  const reviewCount = latestRatings.length || 0;

  // Parse modes to check availability
  const availableModes = modes ? modes.split(',') : [];
  const hasVideo = availableModes.includes('VIDEO');
  const hasInPerson = availableModes.includes('IN_PERSON');
  const hasAudio = availableModes.includes('AUDIO');

  const fullName = `${title} ${firstName} ${lastName}`;
  const defaultImage = IMAGES.user_filled;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#374151]/90 z-[9998]"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
        <div
          className="bg-white rounded-2xl max-w-4xl w-full max-h-[calc(90*var(--vh))] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Dialog Content */}
          <div className="p-6 sm:p-8">
            {/* Close Button */}
            <div className="flex justify-end mb-4">
              <button
                onClick={onClose}
                className="flex items-center justify-center w-[32px] h-[32px] p-0 border-0 bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0] hover:text-[#171717] rounded-full transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Main Content */}
            <div className="flex flex-col gap-4">
              {/* Top Section - Image and Basic Info */}
              <div className="flex gap-8 flex-col sm:flex-row">
                {/* Profile Image */}
                <div className="w-[160px] h-[160px] sm:w-[200px] sm:h-[200px] mx-auto sm:mx-0 rounded-xl border-[1px] border-solid border-[#E2E8F0] bg-[#F8FAFC] overflow-hidden flex-shrink-0">
                  <img
                    src={imageURL || defaultImage}
                    alt={fullName}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      e.target.src = defaultImage;
                    }}
                  />
                </div>

                {/* Counselor Details */}
                <div className="flex-1 flex flex-col gap-3">
                  {/* Name and Status Badges */}
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <h2 className="text-2xl font-bold text-[#171717] font-poppins mb-0">
                      {fullName}
                    </h2>
                    <div className="flex gap-3">
                      {/* Online Badge - Only show if VIDEO mode is available */}
                      {hasVideo && (
                        <div className="bg-[#EFF6FF] px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <img src={IMAGES.video_icon} alt="online" className="w-3 h-3 object-contain" />
                          </div>
                          <span className="text-[11px] font-semibold text-[#2563EB] font-poppins">
                            Online
                          </span>
                        </div>
                      )}
                      {/* In-person Badge - Only show if IN_PERSON mode is available */}
                      {hasInPerson && (
                        <div className="bg-[#EFF6FF] px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <img src={IMAGES.in_person} alt="in-person" className="w-3 h-3 object-contain" />
                          </div>
                          <span className="text-[11px] font-semibold text-[#2563EB] font-poppins">
                            In-person
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Degree */}
                  {degree && degree.length > 0 && (
                    <div className="flex flex-col justify-center">
                      <p className="text-sm font-semibold text-[#4B61FF] font-poppins mb-0">
                        {degree.join(', ')}
                      </p>
                    </div>
                  )}

                  {/* Experience */}
                  {experience && (
                    <p className="text-sm text-[#171717] font-poppins ">
                      {experience}+ years of experience
                    </p>
                  )}

                  {/* Languages */}
                  {languages && languages.length > 0 && (
                    <div className="flex items-center gap-2">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
                        <circle cx="8" cy="8" r="6" stroke="#2563EB" strokeWidth="1.2" fill="none" />
                        <path d="M2 8h12M8 2a9 9 0 0 1 0 12M8 2a9 9 0 0 0 0 12" stroke="#2563EB" strokeWidth="1.2" fill="none" />
                      </svg>
                      <span className="text-sm text-[#171717] font-poppins">
                        {languages.join(", ")}
                      </span>
                    </div>
                  )}

                  {/* Specialization */}
                  {specializations && specializations.length > 0 && (
                    <div className="flex items-center gap-2 overflow-x-auto">
                      <span className="text-sm text-[#64748B] font-poppins flex-shrink-0">
                        Specialisation
                      </span>
                      <div className="flex gap-2 overflow-x-auto">
                        {specializations.map((spec, index) => (
                          <div
                            key={index}
                            className="bg-[#EFF6FF] px-2.5 py-0.5 rounded-full flex-shrink-0 flex items-center"
                          >
                            <span className="text-xs font-semibold text-[#2563EB] font-poppins whitespace-nowrap">
                              {spec}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {about && (
                    <p className="text-xs text-[#64748B] font-poppins leading-5">
                      {description}
                    </p>
                  )}

                  {/* Hospital/Location */}
                  <div className="flex items-center gap-1">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
                      <path
                        d="M8 1.33334C5.42267 1.33334 3.33333 3.42267 3.33333 6C3.33333 9.5 8 14.6667 8 14.6667C8 14.6667 12.6667 9.5 12.6667 6C12.6667 3.42267 10.5773 1.33334 8 1.33334Z"
                        stroke="#2563EB"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                      <circle cx="8" cy="6" r="1.5" stroke="#2563EB" strokeWidth="1.2" fill="none" />
                    </svg>
                    <span className="text-sm text-[#171717] font-poppins">
                      {hospital}
                    </span>
                  </div>

                  {/* Stats Section */}
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    {/* Rating */}
                    {rating && (
                      <div className="bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] px-1 py-1 rounded-xl flex items-center gap-2">
                        <div className="bg-white p-1 rounded-full">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                              fill="#F59E0B"
                            />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-[#171717] font-poppins pr-2">
                          {rating}
                        </span>
                      </div>
                    )}

                    {/* Next Available Slot */}
                    {nextAvailableSlot && (
                      <div className="bg-[#EFF6FF] border-[1px] border-solid border-[#BFDBFE] px-3 py-1.5 rounded-xl flex items-center gap-2">
                        <span className="text-xs text-[#64748B] font-poppins">Next available:</span>
                        <span className="text-sm font-semibold font-poppins text-[#2563EB]">
                          {nextAvailableSlot}
                        </span>
                      </div>
                    )}

                    {/* Session Count */}
                    <div className="bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] px-1 py-1 rounded-xl flex items-center gap-2">
                      <div className="bg-white p-1 rounded-full">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <img src={IMAGES.video_icon} alt="video" className="w-4 h-4 object-contain" />
                        </div>
                      </div>
                      <span className="text-xs text-[#64748B] font-poppins pr-2">
                        <span className="text-sm text-[#171717] font-medium">
                          {consultationCount || 0}+
                        </span>{""}
                        Consultations
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PractitionerProfileDialog;
